/**
 * MmAccessGuard
 * V60: MM관리 접근 가드
 * V113: MM 마스터 설정 접근 추가
 * V114.3: role_tl 역할 부여자 MM작성 접근 허용
 * V120.3: 롤만 SSoT - jobTitleLevel 기반 접근 제거
 *
 * MM관리(/mm/*) 경로에 대한 접근 권한을 검증합니다.
 *
 * **V120.3 접근 정책 (롤만 SSoT):**
 * - /mm/master (마스터): role_exec, admin만 허용 (V113)
 * - /mm/overview (현황): role_manager, role_exec, admin만 허용
 * - /mm/register (등록): role_manager, role_exec, admin, role_tl만 허용
 *
 * **허용 역할 (auth_roleAssignments 기반):**
 * - system_admin, role_admin_kr, role_admin_cn, role_hr (관리자급)
 * - role_manager (조직장)
 * - role_exec (임원)
 * - role_tl (테스트 리더) - /mm/register만 접근 가능
 *
 * **V120.3 정책 변경:**
 * - jobTitleLevel 기반 접근 제거 (TEAM_LEADER 직책만으로 접근 불가)
 * - 반드시 역할(role_tl 등)을 부여받아야 접근 가능
 * - FE/BE 권한 조건 완전 동기화 (SSoT: auth_roleAssignments)
 *
 * **V113 마스터 규칙:**
 * - /mm/master는 role_exec + admin만 허용 (role_manager 불가)
 */

import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthorization } from '../shared/hooks/useAuthorization';
import { ADMIN_LIKE_ROLES } from '../types/role';

interface MmAccessGuardProps {
  children: React.ReactElement;
}

/**
 * V120.3: MM 전체 접근 허용 역할 (롤만 SSoT)
 *
 * MM 현황(/mm/overview) 및 마스터(/mm/master) 접근 허용 역할
 * - role_tl은 /mm/register만 접근 가능하므로 여기에 포함되지 않음
 */
const MM_FULL_ACCESS_ROLES = [
  'system_admin',
  'role_admin_kr',
  'role_admin_cn',
  'role_manager',
  'role_exec',
] as const;

export function MmAccessGuard({ children }: MmAccessGuardProps) {
  const location = useLocation();
  const { user, hasRole, isLoading: authLoading } = useAuthorization();

  // 현재 경로 확인
  const currentPath = location.pathname;
  const isMasterPath = currentPath === '/mm/master' || currentPath.startsWith('/mm/master/'); // V113
  const isOverviewPath = currentPath === '/mm/overview' || currentPath.startsWith('/mm/overview/');
  const isRegisterPath = currentPath === '/mm/register' || currentPath.startsWith('/mm/register/');

  // V60: 전체 접근 역할 여부 확인
  const hasMmFullAccess = useMemo(() => {
    if (authLoading || !user) return null;

    // 1. userType === 'admin'
    const isAdminUser = (user?.profile as any)?.userType === 'admin';
    if (isAdminUser) return true;

    // 2. 관리자급 역할
    const hasAdminLikeRole = ADMIN_LIKE_ROLES.some(roleId => hasRole(roleId));
    if (hasAdminLikeRole) return true;

    // 3. MM 전체 접근 역할 체크
    const hasFullAccessRole = MM_FULL_ACCESS_ROLES.some(roleId => hasRole(roleId));
    if (hasFullAccessRole) return true;

    return false;
  }, [user, hasRole, authLoading]);

  // V113: MM 마스터 접근 역할 확인 (role_exec + admin만)
  const hasMmMasterAccess = useMemo(() => {
    if (authLoading || !user) return null;

    // 1. userType === 'admin'
    const isAdminUser = (user?.profile as any)?.userType === 'admin';
    if (isAdminUser) return true;

    // 2. 관리자급 역할
    const hasAdminLikeRole = ADMIN_LIKE_ROLES.some(roleId => hasRole(roleId));
    if (hasAdminLikeRole) return true;

    // 3. role_exec만 허용 (role_manager는 불가)
    if (hasRole('role_exec')) return true;

    return false;
  }, [user, hasRole, authLoading]);

  // V120.3: role_tl 역할 부여 여부 확인
  const hasRoleTl = useMemo(() => {
    if (authLoading || !user) return false;
    return hasRole('role_tl');
  }, [user, hasRole, authLoading]);

  // V120.3: 접근 가능 여부 판정 (롤만 SSoT)
  const canAccess = useMemo(() => {
    // V113: 마스터 경로는 별도 권한 체크
    if (isMasterPath) {
      return hasMmMasterAccess === true;
    }

    // 전체 접근 역할이면 모든 MM 경로 허용
    if (hasMmFullAccess === true) return true;

    // V120.3: role_tl 역할 부여된 경우: 등록만 허용
    // jobTitleLevel 기반 접근 제거 - 반드시 역할이 있어야 함
    if (hasRoleTl) {
      // /mm/register만 허용
      if (isRegisterPath) return true;
      // /mm/overview, /mm/master 차단
      return false;
    }

    return false;
  }, [hasMmFullAccess, hasMmMasterAccess, hasRoleTl, isRegisterPath, isMasterPath]);

  // V236: 전체 접근 역할이라도 canAccess 체크 필요 (mm/master는 role_manager 불가)
  // 로딩 중
  if (authLoading || hasMmFullAccess === null) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px',
        color: 'var(--color-gray-500)',
      }}>
        권한 확인 중...
      </div>
    );
  }

  // 접근 불가
  if (!canAccess) {
    console.warn(
      `[MmAccessGuard] V120.3 접근 차단 (롤만 SSoT): ` +
      `path=${currentPath}, ` +
      `userType=${(user?.profile as any)?.userType}, ` +
      `hasMmFullAccess=${hasMmFullAccess}, ` +
      `hasMmMasterAccess=${hasMmMasterAccess}, ` +
      `hasRoleTl=${hasRoleTl}`
    );

    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
      }}>
        <div style={{
          fontSize: '48px',
          marginBottom: '16px',
        }}>
          🔒
        </div>
        <h2 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'var(--color-gray-800)',
          marginBottom: '12px',
        }}>
          접근 권한이 없습니다
        </h2>
        <p style={{
          color: 'var(--color-gray-600)',
          marginBottom: '8px',
        }}>
          {isOverviewPath
            ? 'MM 현황은 조직장(role_manager) 또는 임원(role_exec) 역할이 필요합니다.'
            : 'MM관리 메뉴는 조직장, 임원, 또는 TL 역할이 필요합니다.'}
        </p>
        <p style={{
          color: 'var(--color-gray-500)',
          fontSize: '14px',
          marginBottom: '24px',
        }}>
          권한이 필요하시면 관리자에게 문의하세요.
        </p>
        <div style={{ marginTop: '24px' }}>
          <button
            onClick={() => window.history.back()}
            style={{
              padding: '8px 24px',
              background: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              marginRight: '8px',
            }}
          >
            뒤로 가기
          </button>
          <button
            onClick={() => window.location.href = '/dashboard'}
            style={{
              padding: '8px 24px',
              background: 'var(--color-gray-200)',
              color: 'var(--color-gray-700)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            대시보드로 이동
          </button>
        </div>
      </div>
    );
  }

  // 접근 허용
  return children;
}
